import { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import roleAccessConfig, { PERMISSIONS } from '../config/roleAccessConfig';

// Backward-compatible groups export derived from config.
const GROUPS = roleAccessConfig.roles.reduce((acc, role) => {
  acc[role.key] = role.group;
  return acc;
}, {});

const rolesByGroup = roleAccessConfig.roles.reduce((acc, role) => {
  acc[role.group] = role;
  return acc;
}, {});

const normalizeGroups = (groupsClaim) => {
  if (Array.isArray(groupsClaim)) return groupsClaim;
  if (typeof groupsClaim === 'string') {
    return groupsClaim
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const useUserPermissions = () => {
  const [userGroups, setUserGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getUserGroups = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get auth session to access the token
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken;
        const accessToken = tokens?.accessToken;

        if (idToken || accessToken) {
          // Prefer the ID token: the pre-token-generation Lambda (GroupMapperLambda)
          // maps Azure AD groups -> Cognito groups via groupOverrideDetails which only
          // modifies the ID token's cognito:groups claim.
          const preferredToken = idToken || accessToken;
          const payload = JSON.parse(atob(preferredToken.toString().split('.')[1]));

          // Try both cognito:groups (direct Cognito) and custom:groups (SAML)
          const groups = normalizeGroups(payload['cognito:groups'] || payload['custom:groups']);
          console.debug('[useUserPermissions] raw token groups:', groups);
          setUserGroups(groups);
        } else {
          setUserGroups([]);
        }
      } catch (err) {
        console.error('Error getting user groups:', err);
        setError(err.message);
        setUserGroups([]);
      } finally {
        setIsLoading(false);
      }
    };

    getUserGroups();
  }, []);

  const activeRole = userGroups
    .map((group) => rolesByGroup[group])
    .filter(Boolean)
    .reduce((selectedRole, currentRole) => {
      if (!selectedRole) {
        return currentRole;
      }

      return currentRole.priority < selectedRole.priority ? currentRole : selectedRole;
    }, null);

  console.debug(
    '[useUserPermissions] unmatched groups:',
    userGroups.filter((g) => !rolesByGroup[g])
  );
  console.debug('[useUserPermissions] active role:', activeRole?.key ?? 'null (no matching group)');

  const hasRole = (roleKey) => activeRole?.key === roleKey;

  const hasPermission = (permission) =>
    Array.isArray(activeRole?.permissions) && activeRole.permissions.includes(permission);

  // Permission helper functions
  const isAdmin = () => hasRole('ADMIN');

  const isOpsEngg = () => hasRole('OPS_ENGG');

  const isOpsSpecialist = () => hasRole('OPS_SPECIALIST');

  const isProductOwner = () => hasRole('PRODUCT_OWNER');

  const isEngineeringLead = () => hasRole('ENGINEERING_LEAD');

  const canAccessIncidentResolver = () => hasPermission(PERMISSIONS.INCIDENT_RESOLVER);
  const canAccessSnAnalysis = () => hasPermission(PERMISSIONS.SN_ANALYSIS);

  const isIndiaFrontline = () => hasRole('INDIA_FRONTLINE');
  const isFinlandFrontline = () => hasRole('FINLAND_FRONTLINE');

  // Can this user invoke the Service Need (SN / PRM) agent?
  const canInvokeServiceNeedAgent = () => {
    return hasPermission(PERMISSIONS.INVOKE_SN_AGENT);
  };

  // Can this user invoke the CEP (DevOps Orchestrator) agent?
  const canInvokeCEPAgent = () => {
    return hasPermission(PERMISSIONS.INVOKE_CEP_AGENT);
  };

  // Check if user can create/edit/delete
  const canModify = () => {
    return hasPermission(PERMISSIONS.MODIFY);
  };

  // Check if user has read-only access
  const isReadOnly = () => {
    return canAccessIncidentResolver() && !canModify();
  };

  // Production performance access permissions
  const canAccessProdPerformance = () => {
    return hasPermission(PERMISSIONS.PROD_PERFORMANCE);
  };

  // Admin, Product Owner, and Engineering Lead share the same prod-performance behavior.
  const canRunProdPerformanceAnalysis = () => {
    return canAccessProdPerformance();
  };

  // Tribe performance access permissions
  // (Admin, Operations Specialist, Product Owner, Engineering Lead).
  const canAccessTribePerformance = () => {
    return hasPermission(PERMISSIONS.TRIBE_PERFORMANCE);
  };

  // AI Governance access permissions — everyone except the Frontline roles
  // (Admin, Operations Engineer, Operations Specialist, Product Owner,
  // Engineering Lead).
  const canAccessAiGovernance = () => {
    return hasPermission(PERMISSIONS.AI_GOVERNANCE);
  };

  // Runbooks access permissions.
  const canAccessRunbooks = () => {
    return hasPermission(PERMISSIONS.RUNBOOKS);
  };

  // Get user role display name
  const getUserRole = () => {
    return activeRole?.displayName || roleAccessConfig.defaultRoleLabel || 'User';
  };

  return {
    userGroups,
    isLoading,
    error,
    // Permission checks
    isAdmin,
    isOpsEngg,
    isOpsSpecialist,
    isProductOwner,
    isEngineeringLead,
    canAccessIncidentResolver,
    canAccessSnAnalysis,
    canModify,
    isReadOnly,
    canAccessProdPerformance,
    canRunProdPerformanceAnalysis,
    canAccessTribePerformance,
    canAccessAiGovernance,
    canAccessRunbooks,
    getUserRole,
    isIndiaFrontline,
    isFinlandFrontline,
    canInvokeServiceNeedAgent,
    canInvokeCEPAgent,
    // Constants for external use
    GROUPS,
  };
};

export default useUserPermissions;
